/* Bernard James — "Request Appointment" booking drawer (BJ-DS, Figma
   "Shopify Final" 1845:10124 "Book Appointment Panel", 27 states; flow and
   copy reconciled with the Figma Make prototype).

   <bj-booking-drawer> subclasses the shared BjDrawer base exported by
   assets/bj-filter-drawer.js (window.BjDrawer): open/close, scrim, body
   scroll lock, focus trap and focus return come from there. Everything in
   this file is the questionnaire itself:

     · a branching step list (computeSteps) — project → approach →
       [ring-status → ring-upload] → budget → details → contact → meeting →
       time → confirmation;
     · one renderer per step, building DOM from the JSON config the snippet
       emits (snippets/bj-booking-drawer.liquid), never from innerHTML with
       user data;
     · a Cal.com API v2 client. Both calls are the PUBLIC, unauthenticated
       endpoints (verified 2026-10-12: GET /v2/slots 200 without auth,
       POST /v2/bookings reaches the event-type lookup without auth, CORS
       `*`). No API key is shipped — a key in storefront JS would be public,
       and nothing here needs one. Unauthenticated calls are rate-limited to
       120/min, so availability is memoised per month and only refetched on
       retry or after a conflict.

   "Pending appointment" / "we'll confirm" semantics depend on the Cal.com
   event types having "Requires confirmation" on — the drawer only requests.

   Close rules: X, scrim and Escape go through requestClose(), which shows
   the in-panel "Are you sure you want to leave?" dialog once any answer
   exists; EXIT and the confirmation's CLOSE reset the state so the next
   open starts fresh.

   Triggers: a delegated document click on any same-origin link whose path
   ends with the configured trigger path (default /pages/private-appointments)
   opens the drawer; the href stays as the no-JS / not-configured fallback.
   window.bjOpenBooking() is exposed for other scripts. */

(() => {
	'use strict';

	if (customElements.get('bj-booking-drawer')) return;

	const Base = window.BjDrawer;
	if (typeof Base !== 'function') return;

	/* Prototype auto-advances a single-select answer after a short beat so
	   the selected state is seen before the step slides away. */
	const AUTO_ADVANCE_MS = 250;
	const STEP_ANIM_MS = 200;

	const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const PHONE_RE = /^[()\d\s-]{7,}$/;
	const E164_RE = /^\+\d{7,15}$/;

	/* ------------------------------------------------------------------ */
	/* Cal.com API v2 client                                                */
	/* ------------------------------------------------------------------ */

	const CAL = {
		base: 'https://api.cal.com/v2',
		/* Per-endpoint versions — Cal.com pins behaviour by this header and
		   silently falls back to an older contract when it is missing. */
		slotsVersion: '2024-09-04',
		bookingVersion: '2024-08-13',
		cache: new Map(), // `${eventTypeId}|${tz}|${yyyy-mm}` -> { at, days }
		ttl: 5 * 60 * 1000,
		inflight: null,

		key(ref, tz, yyyyMm) {
			return `${ref.key}|${tz}|${yyyyMm}`;
		},

		invalidate(ref, tz, yyyyMm) {
			this.cache.delete(this.key(ref, tz, yyyyMm));
		},

		/* Resolves to { 'YYYY-MM-DD': [isoStart, ...] } for the days of the
		   month that have at least one slot. `end` is inclusive on Cal's side
		   (verified), so the last day of the month is passed as-is. */
		async slots(ref, tz, yyyyMm, { force } = {}) {
			const key = this.key(ref, tz, yyyyMm);
			const hit = this.cache.get(key);
			if (!force && hit && Date.now() - hit.at < this.ttl) return hit.days;

			const today = todayKey(tz);
			const { days } = monthGrid(yyyyMm);
			const first = days[0];
			const last = days[days.length - 1];
			if (last < today) {
				this.cache.set(key, { at: Date.now(), days: {} });
				return {};
			}

			if (this.inflight) this.inflight.abort();
			const controller = new AbortController();
			this.inflight = controller;

			const url = new URL(`${this.base}/slots`);
			Object.keys(ref.params).forEach((k) => url.searchParams.set(k, String(ref.params[k])));
			url.searchParams.set('start', first < today ? today : first);
			url.searchParams.set('end', last);
			url.searchParams.set('timeZone', tz);

			const res = await fetch(url.toString(), {
				headers: { 'cal-api-version': this.slotsVersion },
				signal: controller.signal,
			});
			if (this.inflight === controller) this.inflight = null;
			if (!res.ok) {
				const err = new Error(`slots ${res.status}`);
				err.status = res.status;
				throw err;
			}

			const json = await res.json();
			// Tolerate both documented shapes: data keyed by day, or data.slots.
			const raw =
				(json && json.data && (Array.isArray(json.data) ? null : json.data.slots || json.data)) || {};
			const out = {};
			Object.keys(raw).forEach((day) => {
				if (!day.startsWith(yyyyMm)) return;
				const list = Array.isArray(raw[day]) ? raw[day] : [];
				const starts = list
					.map((s) => (typeof s === 'string' ? s : s && (s.start || s.time)))
					.filter(Boolean);
				if (starts.length) out[day] = starts;
			});

			this.cache.set(key, { at: Date.now(), days: out });
			return out;
		},

		async book(payload) {
			const res = await fetch(`${this.base}/bookings`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'cal-api-version': this.bookingVersion,
				},
				body: JSON.stringify(payload),
			});
			let json = null;
			try {
				json = await res.json();
			} catch (e) {
				json = null;
			}
			if (!res.ok || !json || json.status === 'error') {
				const detail = (json && json.error) || {};
				const err = new Error(detail.message || `booking ${res.status}`);
				err.status = res.status;
				err.code = detail.code || '';
				throw err;
			}
			return json.data;
		},
	};

	/* ------------------------------------------------------------------ */
	/* Klaviyo — after a booking succeeds the contact joins a list via the  */
	/* public client endpoint (company id only, no private key; CORS *).   */
	/* Revision must be 2025-01-15 or later: 2024-10-15 rejects the       */
	/* profile.subscriptions consent object with "not a valid field".      */
	/* Bridal paths (engagement ring / wedding band) go to one list, every  */
	/* other path to the other. Email marketing consent only — no SMS.     */
	/* Gated by the consent checkbox on the time step (renderConsent).     */
	/* Fire-and-forget: a failure never touches the booking confirmation.  */
	/* ------------------------------------------------------------------ */

	const KLAVIYO = {
		base: 'https://a.klaviyo.com/client/subscriptions',
		revision: '2025-10-15',

		async subscribe(companyId, listId, profile, source) {
			const url = `${KLAVIYO.base}?company_id=${encodeURIComponent(companyId)}`;
			const body = {
				data: {
					type: 'subscription',
					attributes: {
						custom_source: source,
						profile: {
							data: {
								type: 'profile',
								attributes: Object.assign({}, profile, {
									subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
								}),
							},
						},
					},
					relationships: { list: { data: { type: 'list', id: listId } } },
				},
			};
			const res = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', revision: KLAVIYO.revision },
				body: JSON.stringify(body),
				keepalive: true,
			});
			if (!res.ok) throw new Error(`klaviyo ${res.status}`);
		},
	};

	/* ------------------------------------------------------------------ */
	/* Date / time-zone helpers — Intl only, no library. Calendar math     */
	/* runs on 'YYYY-MM-DD' strings via Date.UTC so the visitor's own zone  */
	/* never shifts a day.                                                  */
	/* ------------------------------------------------------------------ */

	const pad = (n) => String(n).padStart(2, '0');

	function visitorTz() {
		try {
			return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
		} catch (e) {
			return 'America/New_York';
		}
	}

	function safeTz(tz) {
		try {
			new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
			return tz;
		} catch (e) {
			return 'America/New_York';
		}
	}

	// en-CA formats as YYYY-MM-DD.
	function ymdInTz(date, tz) {
		return new Intl.DateTimeFormat('en-CA', {
			timeZone: tz,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}).format(date);
	}

	const todayKey = (tz) => ymdInTz(new Date(), tz);

	function parseYmd(ymd) {
		const [y, m, d] = ymd.split('-').map(Number);
		return Date.UTC(y, m - 1, d);
	}
	const fromUtc = (ms) => new Date(ms).toISOString().slice(0, 10);
	const addDays = (ymd, n) => fromUtc(parseYmd(ymd) + n * 864e5);
	// Monday = 0 … Sunday = 6, matching the Mo…Su header.
	const weekdayMon0 = (ymd) => (new Date(parseYmd(ymd)).getUTCDay() + 6) % 7;

	function nextWeekday(ymd) {
		let k = addDays(ymd, 1);
		while (weekdayMon0(k) > 4) k = addDays(k, 1);
		return k;
	}

	function monthGrid(yyyyMm) {
		const [y, m] = yyyyMm.split('-').map(Number);
		const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
		return {
			lead: weekdayMon0(`${yyyyMm}-01`),
			days: Array.from({ length: count }, (_, i) => `${yyyyMm}-${pad(i + 1)}`),
		};
	}

	function shiftMonth(yyyyMm, n) {
		const [y, m] = yyyyMm.split('-').map(Number);
		return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7);
	}

	const monthLabel = (yyyyMm) =>
		new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
			new Date(`${yyyyMm}-01T00:00:00Z`)
		);

	// "November 7" — for the in-person closed note.
	const longDate = (ymd) =>
		new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' }).format(
			new Date(parseYmd(ymd))
		);

	const dayAria = (ymd) =>
		new Intl.DateTimeFormat('en-US', {
			weekday: 'long',
			month: 'long',
			day: 'numeric',
			timeZone: 'UTC',
		}).format(new Date(parseYmd(ymd)));

	const fmtTime = (iso, tz) =>
		new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(
			new Date(iso)
		);

	const fmtLongDate = (iso, tz) =>
		new Intl.DateTimeFormat('en-US', {
			timeZone: tz,
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		}).format(new Date(iso));

	/* "Pacific time" from America/Los_Angeles; falls back to the IANA city
	   when the platform only knows a GMT offset name. */
	function tzLabel(tz, suffix) {
		let name = '';
		try {
			const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' })
				.formatToParts(new Date())
				.find((p) => p.type === 'timeZoneName');
			name = part ? part.value : '';
		} catch (e) {
			name = '';
		}
		name = name.replace(/\s(Standard|Daylight|Summer)\sTime$/i, '').replace(/\sTime$/i, '');
		if (!name || /^(GMT|UTC)|^[+-]\d/.test(name)) {
			name = tz.split('/').pop().replace(/_/g, ' ');
		}
		return suffix ? `${name} ${suffix}` : name;
	}

	/* ------------------------------------------------------------------ */
	/* Small DOM helper — attributes, listeners, text; children may be     */
	/* nodes, strings or nested arrays. No innerHTML anywhere.             */
	/* ------------------------------------------------------------------ */

	function el(tag, attrs, ...children) {
		const node = document.createElement(tag);
		if (attrs) {
			Object.keys(attrs).forEach((k) => {
				const v = attrs[k];
				if (v == null || v === false) return;
				if (k === 'class') node.className = v;
				else if (k === 'text') node.textContent = v;
				else if (k === 'dataset') Object.assign(node.dataset, v);
				else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
				else node.setAttribute(k, v === true ? '' : String(v));
			});
		}
		children.flat(Infinity).forEach((c) => {
			if (c == null || c === false) return;
			node.append(c.nodeType ? c : document.createTextNode(String(c)));
		});
		return node;
	}

	function decodeStrings(strings) {
		const box = document.createElement('textarea');
		const out = {};
		Object.keys(strings).forEach((k) => {
			const v = strings[k];
			if (typeof v !== 'string' || v.indexOf('&') === -1) {
				out[k] = v;
				return;
			}
			box.innerHTML = v;
			out[k] = box.value;
		});
		return out;
	}

	const fill = (template, vars) =>
		String(template || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));

	/* ------------------------------------------------------------------ */
	/* State                                                                */
	/* ------------------------------------------------------------------ */

	function initialState(cfg) {
		return {
			path: '',
			approach: '',
			existingRing: '',
			existingRingDetails: '',
			budget: '',
			timeline: '',
			collection: [],
			stone: [],
			description: '',
			firstName: '',
			lastName: '',
			email: '',
			countryCode: (cfg.options.countryCodes && cfg.options.countryCodes[0] && cfg.options.countryCodes[0].dial) || '+1',
			countryIso: (cfg.options.countryCodes && cfg.options.countryCodes[0] && cfg.options.countryCodes[0].iso) || 'US',
			codeTouched: false, // user picked a dial code by hand → stop syncing it from Country
			phone: '',
			country: '',
			state: '',
			city: '',
			meeting: '', // 'in-person' | 'virtual'
			timeZone: '',
			month: '', // 'YYYY-MM' shown in the calendar
			date: '', // 'YYYY-MM-DD'
			slot: null, // { start: ISO string }
			stepIndex: 0,
			submitting: false,
			submitError: '',
			marketingOptIn: false, // consent checkbox on the time step; gates the Klaviyo subscribe
			booked: null,
			errors: {},
			slotsStatus: 'idle', // idle | loading | ready | error
			slots: {},
		};
	}

	function computeSteps(s) {
		const steps = ['project'];
		if (s.path && s.path !== 'Not sure yet') steps.push('approach');
		if (s.path === 'Wedding band' && s.approach === 'Designed around my engagement ring') {
			steps.push('ring-status');
			if (s.existingRing === 'Yes') steps.push('ring-upload');
		}
		return steps.concat(['budget', 'details', 'contact', 'meeting', 'time', 'confirmation']);
	}

	/* A Cal.com event type can be referenced three ways in theme settings:
	   a numeric id (123), the short id Cal.com's UI now shows
	   (1cELHmqM91rXprPao1aNpC — /v2/slots accepts it as eventTypeId, but
	   /v2/bookings rejects it: "must be an integer", verified 2026-10-13, so
	   it can only ever show availability), or the public booking link
	   (https://cal.com/<user>/<event>, or https://cal.com/team/<team>/<event>),
	   which maps to the slug parameters BOTH endpoints accept — the form the
	   settings copy recommends. Returns null when blank. */
	function parseEventRef(value) {
		const raw = String(value == null ? '' : value).trim();
		if (!raw) return null;
		if (/^\d+$/.test(raw)) return { key: raw, params: { eventTypeId: Number(raw) } };
		let path = raw;
		if (/^https?:\/\//i.test(raw) || /^[\w.-]+\.[a-z]{2,}\//i.test(raw)) {
			try {
				path = new URL(/^https?:/i.test(raw) ? raw : `https://${raw}`).pathname;
			} catch (e) {
				path = raw;
			}
		}
		const parts = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
		if (parts.length >= 2) {
			if (parts[0] === 'team' && parts.length >= 3) {
				return { key: raw, params: { teamSlug: parts[1], eventTypeSlug: parts[2] } };
			}
			if (parts[0] === 'org' && parts.length >= 4) {
				return { key: raw, params: { organizationSlug: parts[1], username: parts[2], eventTypeSlug: parts[3] } };
			}
			return { key: raw, params: { username: parts[0], eventTypeSlug: parts[1] } };
		}
		return { key: raw, params: { eventTypeId: raw } };
	}

	/* Prototype's display labels for the stored path value. */
	function pathLabel(path) {
		if (path === 'Engagement rings') return 'Engagement ring';
		if (path === 'Wedding band') return 'Wedding band(s)';
		if (path === 'Not sure yet') return "I'm not sure yet";
		return path;
	}

	function budgetList(s, options) {
		if (s.path === 'Engagement rings' && s.approach === 'Bespoke, made from scratch') {
			return options.budget.engagementBespoke;
		}
		if (s.path === 'Engagement rings') return options.budget.engagement;
		if (s.path === 'Wedding band') return options.budget.band;
		return options.budget.other;
	}

	/* ------------------------------------------------------------------ */
	/* The element                                                          */
	/* ------------------------------------------------------------------ */

	class BjBookingDrawer extends Base {
		constructor() {
			super();
			this.onDocumentKeydown = null;
			this.autoTimer = 0;
			this.confirmOpen = false;
			this.lastFocus = null;
		}

		connectedCallback() {
			super.connectedCallback();

			const configEl = this.querySelector('[data-bj-booking-config]');
			try {
				this.cfg = JSON.parse(configEl ? configEl.textContent : '{}');
			} catch (e) {
				this.cfg = null;
			}
			if (!this.cfg || !this.cfg.options) {
				this.cfg = null;
				return;
			}
			/* Shopify's `t` filter HTML-escapes its output ("You&#39;re"), and
			   these strings land in textContent, so decode once here. A
			   detached <textarea> decodes without executing anything. */
			this.cfg.strings = decodeStrings(this.cfg.strings || {});
			this.cfg.inPerson = this.cfg.inPerson || {};
			this.cfg.inPerson.timeZone = safeTz(this.cfg.inPerson.timeZone || 'America/New_York');
			/* Last bookable in-person day (the USM SoHo residency ends
			   2026-11-07). Cal.com simply has no slots past it, which would
			   read as empty months; with a date the UI can say why. */
			this.cfg.inPerson.until = /^\d{4}-\d{2}-\d{2}$/.test(this.cfg.inPerson.until || '') ? this.cfg.inPerson.until : '';
			this.cfg.inPerson.closedNote = this.cfg.inPerson.closedNote || '';
			this.cfg.klaviyo = this.cfg.klaviyo || {};
			this.ids = {
				'in-person': parseEventRef(this.cfg.eventTypes && this.cfg.eventTypes.inPerson),
				virtual: parseEventRef(this.cfg.eventTypes && this.cfg.eventTypes.virtual),
			};
			if (!this.ids['in-person'] && !this.ids.virtual) {
				// Enabled in settings but nothing to book against: links keep
				// navigating to the contact page. Say so where a developer looks.
				console.warn('[bj-booking] No Cal.com event type configured; booking drawer stays dormant.');
			}

			this.stepEl = this.querySelector('[data-bj-booking-step]');
			this.bodyEl = this.querySelector('[data-bj-booking-body]');
			this.navEl = this.querySelector('.bj-booking__nav');
			this.progressEl = this.querySelector('[data-bj-booking-progress]');
			this.backBtn = this.querySelector('[data-bj-booking-back]');
			this.liveEl = this.querySelector('[data-bj-booking-live]');
			this.confirmEl = this.querySelector('[data-bj-booking-confirm]');

			this.templates = {};
			this.querySelectorAll('template[data-bj-booking-tpl]').forEach((t) => {
				this.templates[t.getAttribute('data-bj-booking-tpl')] = t;
			});
			this.iconSet = {};
			if (this.templates.icons) {
				this.templates.icons.content.querySelectorAll('[data-icon]').forEach((span) => {
					this.iconSet[span.getAttribute('data-icon')] = span;
				});
			}

			if (this.backBtn) this.backBtn.addEventListener('click', () => this.back());
			const stay = this.querySelector('[data-bj-booking-confirm-stay]');
			const exit = this.querySelector('[data-bj-booking-confirm-exit]');
			if (stay) stay.addEventListener('click', () => this.hideConfirm());
			if (exit) exit.addEventListener('click', () => this.forceClose());

			this.state = initialState(this.cfg);
			this.renderers = {
				project: () => this.renderOptionList('project'),
				approach: () => this.renderOptionList('approach'),
				'ring-status': () => this.renderOptionList('ring-status'),
				'ring-upload': () => this.renderNotesStep('ring-upload'),
				budget: () => this.renderBudget(),
				details: () => this.renderNotesStep('details'),
				contact: () => this.renderContact(),
				meeting: () => this.renderOptionList('meeting'),
				time: () => this.renderTime(),
				confirmation: () => this.renderConfirmation(),
			};
		}

		get triggerPath() {
			return ((this.cfg && this.cfg.triggerPath) || '/pages/private-appointments').replace(/\/$/, '');
		}

		get ready() {
			return !!(this.cfg && (this.ids['in-person'] || this.ids.virtual));
		}

		str(key, vars) {
			const s = this.cfg.strings[key];
			return vars ? fill(s, vars) : s == null ? key : s;
		}

		/* '' when there is no cutoff, else 'YYYY-MM-DD'. */
		inPersonUntil() {
			return this.cfg.inPerson.until || '';
		}

		inPersonClosedNote() {
			const until = this.inPersonUntil();
			return until ? fill(this.cfg.inPerson.closedNote, { date: longDate(until) }) : '';
		}

		/* True once the cutoff has passed in the studio's time zone. */
		inPersonClosed() {
			const until = this.inPersonUntil();
			return !!until && todayKey(this.cfg.inPerson.timeZone) > until;
		}

		/* icon-spinner's svg carries the theme's `.spinner` class, which
		   base.css hides inside .button; the CSS re-shows it under
		   .bj-booking__cta-spinner when the button is .is-loading. */
		spinner() {
			const svg = this.icon('spinner');
			if (!svg) return null;
			svg.classList.add('bj-booking__cta-spinner');
			svg.setAttribute('aria-hidden', 'true');
			return svg;
		}

		icon(name) {
			const src = this.iconSet[name];
			if (!src) return null;
			const svg = src.querySelector('svg') || src.firstElementChild;
			return svg ? svg.cloneNode(true) : null;
		}

		emit(type, detail) {
			document.dispatchEvent(
				new CustomEvent('bj:booking', { detail: Object.assign({ type }, detail || {}) })
			);
		}

		/* ---------------------------------------------------------------- */
		/* Open / close                                                      */
		/* ---------------------------------------------------------------- */

		openFrom(trigger) {
			if (!this.ready) return false;
			const wasOpen = this.isOpen;
			super.openFrom(trigger);
			if (!wasOpen) {
				this.render(1);
				this.emit('open');
			}
			return true;
		}

		/* The base binds `this.close` in its constructor, so this override is
		   what the scrim, the X and (via onKeydown) Escape call. */
		close() {
			this.requestClose();
		}

		hasAnswers() {
			return this.state.stepIndex > 0 || !!this.state.path;
		}

		requestClose() {
			if (!this.isOpen || this.confirmOpen) return;
			if (this.hasAnswers() && !this.state.booked) this.showConfirm();
			else this.forceClose();
		}

		forceClose() {
			clearTimeout(this.autoTimer);
			if (this.confirmOpen) this.hideConfirm(false);
			if (CAL.inflight) CAL.inflight.abort();
			this.state = initialState(this.cfg);
			super.close();
			this.emit('close');
		}

		showConfirm() {
			if (!this.confirmEl) {
				this.forceClose();
				return;
			}
			this.lastFocus = document.activeElement;
			this.confirmOpen = true;
			this.confirmEl.hidden = false;
			if (this.bodyEl) this.bodyEl.inert = true;
			if (this.navEl) this.navEl.inert = true;
			const stay = this.confirmEl.querySelector('[data-bj-booking-confirm-stay]');
			if (stay) stay.focus();
		}

		hideConfirm(restoreFocus = true) {
			if (!this.confirmEl) return;
			this.confirmOpen = false;
			this.confirmEl.hidden = true;
			if (this.bodyEl) this.bodyEl.inert = false;
			if (this.navEl) this.navEl.inert = false;
			if (restoreFocus) {
				const target =
					this.lastFocus && this.contains(this.lastFocus) && this.lastFocus.offsetParent !== null
						? this.lastFocus
						: this.stepEl.querySelector('[tabindex="-1"]');
				if (target) target.focus();
			}
			this.lastFocus = null;
		}

		focusable() {
			const items = super.focusable();
			if (!this.confirmOpen) return items;
			return items.filter((item) => this.confirmEl.contains(item));
		}

		/* ---------------------------------------------------------------- */
		/* Keyboard                                                          */
		/* ---------------------------------------------------------------- */

		onKeydown(event) {
			if (event.key === 'Escape') {
				event.preventDefault();
				if (this.confirmOpen) this.hideConfirm();
				else this.requestClose();
				return;
			}
			if (event.key === 'Tab') {
				super.onKeydown(event);
				return;
			}
			if (this.confirmOpen || event.metaKey || event.ctrlKey || event.altKey) return;
			const target = event.target;
			if (target && target.matches && target.matches('input, select, textarea, [contenteditable]')) return;

			if (event.key === 'ArrowUp') {
				event.preventDefault();
				this.back();
				return;
			}

			if (/^[a-z]$/i.test(event.key)) {
				const options = this.stepEl.querySelectorAll('.bj-booking__option');
				const index = event.key.toUpperCase().charCodeAt(0) - 65;
				if (options[index]) {
					event.preventDefault();
					options[index].click();
				}
				return;
			}

			const step = this.currentStep();
			if (event.key === 'Enter' && (step === 'details' || step === 'ring-upload')) {
				event.preventDefault();
				this.advance();
			}
		}

		/* ---------------------------------------------------------------- */
		/* Navigation                                                        */
		/* ---------------------------------------------------------------- */

		currentStep() {
			return computeSteps(this.state)[this.state.stepIndex];
		}

		set(field, value) {
			const s = this.state;
			if (s[field] === value) return;
			s[field] = value;
			if (s.errors[field]) delete s.errors[field];
			// Upstream answers reshape the path; drop what no longer applies.
			if (field === 'path') {
				s.approach = '';
				s.existingRing = '';
				s.existingRingDetails = '';
				s.budget = '';
			} else if (field === 'approach') {
				s.existingRing = '';
				s.existingRingDetails = '';
			} else if (field === 'existingRing') {
				s.existingRingDetails = '';
			}
			if (field !== 'slot' && field !== 'submitError') s.booked = null;
		}

		go(index, direction) {
			const steps = computeSteps(this.state);
			const clamped = Math.max(0, Math.min(index, steps.length - 1));
			this.state.stepIndex = clamped;
			this.render(direction);
			this.emit('step', { step: steps[clamped], index: clamped });
		}

		advance() {
			const steps = computeSteps(this.state);
			if (this.state.stepIndex < steps.length - 1) this.go(this.state.stepIndex + 1, 1);
		}

		back() {
			clearTimeout(this.autoTimer);
			if (this.currentStep() === 'confirmation') {
				this.go(computeSteps(this.state).indexOf('time'), -1);
				return;
			}
			if (this.state.stepIndex > 0) this.go(this.state.stepIndex - 1, -1);
		}

		autoAdvance() {
			clearTimeout(this.autoTimer);
			this.autoTimer = setTimeout(() => this.advance(), AUTO_ADVANCE_MS);
		}

		stepTitle(step) {
			const s = this.state;
			switch (step) {
				case 'project':
					return this.str('project_title');
				case 'approach': {
					const group = this.cfg.options.approach[s.path];
					return group ? group.title : '';
				}
				case 'ring-status':
					return this.str('ring_status_title');
				case 'ring-upload':
					return this.str('ring_upload_title');
				case 'budget':
					return this.str('budget_title');
				case 'details':
					return this.str('details_title');
				case 'contact':
					return this.str('contact_title');
				case 'meeting':
					return this.str('meeting_title');
				case 'time':
					return this.str('time_title');
				default:
					return '';
			}
		}

		/* Full step render: swap, animate, progress, focus, announce. */
		render(direction) {
			clearTimeout(this.autoTimer);
			const s = this.state;
			const steps = computeSteps(s);
			if (s.stepIndex > steps.length - 1) s.stepIndex = steps.length - 1;
			const step = steps[s.stepIndex];
			const total = steps.length - 1; // confirmation is not a numbered step
			const current = Math.min(s.stepIndex + 1, total);

			this.paint(step);

			if (this.stepEl) {
				this.stepEl.dataset.direction = direction === -1 ? 'back' : 'forward';
				this.stepEl.classList.remove('is-entering');
				void this.stepEl.offsetWidth;
				this.stepEl.classList.add('is-entering');
			}
			if (this.bodyEl) this.bodyEl.scrollTop = 0;

			if (this.progressEl) {
				this.progressEl.hidden = step === 'confirmation';
				this.progressEl.setAttribute('aria-valuemax', String(total));
				this.progressEl.setAttribute('aria-valuenow', String(current));
				this.progressEl.setAttribute(
					'aria-valuetext',
					this.str('progress', { current, total })
				);
				this.progressEl.style.setProperty('--bj-booking-progress', `${(current / total) * 100}%`);
			}
			if (this.backBtn) this.backBtn.disabled = s.stepIndex === 0;

			const heading = this.stepEl.querySelector('[tabindex="-1"]');
			if (heading) heading.focus({ preventScroll: true });

			const title = this.stepTitle(step) || this.str('all_set_anon');
			this.announce(
				step === 'confirmation' ? title : `${this.str('progress', { current, total })}: ${title}`
			);

			if (step === 'time') this.loadMonth();
		}

		/* Re-render the current step in place (no animation, no scroll), and
		   put focus back on the control that had it, by data-key. */
		rerender() {
			const active = document.activeElement;
			const key = active && this.stepEl.contains(active) ? active.getAttribute('data-key') : null;
			this.paint(this.currentStep());
			if (key) {
				const next = this.stepEl.querySelector(`[data-key="${CSS.escape(key)}"]`);
				if (next && !next.disabled) next.focus({ preventScroll: true });
			}
		}

		paint(step) {
			const frag = document.createDocumentFragment();
			const title = this.stepTitle(step);
			if (title) {
				frag.append(
					el('h3', { class: 'bj-booking__step-title', id: 'BjBookingStepTitle', tabindex: '-1', text: title })
				);
			}
			const renderer = this.renderers[step];
			if (renderer) frag.append(renderer());
			this.stepEl.replaceChildren(frag);
		}

		announce(text) {
			if (!this.liveEl) return;
			this.liveEl.textContent = '';
			setTimeout(() => {
				this.liveEl.textContent = text;
			}, 50);
		}

		/* ---------------------------------------------------------------- */
		/* Renderers                                                         */
		/* ---------------------------------------------------------------- */

		optionsFor(step) {
			const s = this.state;
			const o = this.cfg.options;
			if (step === 'project') {
				return { field: 'path', items: o.project.map((p) => ({ label: p.label, value: p.value })) };
			}
			if (step === 'approach') {
				const group = o.approach[s.path];
				return {
					field: 'approach',
					items: (group ? group.options : []).map((a) => ({ label: a.label, value: a.label, desc: a.desc })),
				};
			}
			if (step === 'ring-status') {
				return { field: 'existingRing', items: o.ringStatus.map((v) => ({ label: v, value: v })) };
			}
			if (step === 'meeting') {
				const items = [];
				if (this.ids['in-person']) {
					const closed = this.inPersonClosed();
					items.push({
						label: this.str('meeting_in_person'),
						value: 'in-person',
						desc: closed ? this.inPersonClosedNote() : this.cfg.inPerson.address,
						disabled: closed,
					});
				}
				if (this.ids.virtual) {
					items.push({ label: this.str('meeting_virtual'), value: 'virtual', desc: this.str('meeting_virtual_desc') });
				}
				return { field: 'meeting', items };
			}
			return { field: '', items: [] };
		}

		renderOptionList(step) {
			const { field, items } = this.optionsFor(step);
			const selected = this.state[field];
			return el(
				'ul',
				{ class: 'bj-booking__options', role: 'list' },
				items.map((item) => {
					const pressed = selected === item.value;
					return el(
						'li',
						null,
						el(
							'button',
							{
								type: 'button',
								class: `bj-booking__option${item.disabled ? ' is-unavailable' : ''}`,
								'aria-pressed': pressed ? 'true' : 'false',
								disabled: item.disabled ? true : null,
								dataset: { value: item.value, key: `opt:${item.value}` },
								onclick: () => this.selectOption(step, field, item.value),
							},
							el(
								'span',
								{ class: 'bj-booking__option-copy' },
								el('span', { class: 'bj-booking__option-label', text: item.label }),
								item.desc ? el('span', { class: 'bj-booking__option-desc', text: item.desc }) : null
							),
							pressed ? el('span', { class: 'bj-booking__option-check', 'aria-hidden': 'true' }, this.icon('check')) : null
						)
					);
				})
			);
		}

		selectOption(step, field, value) {
			if (step === 'meeting') this.setMeeting(value);
			else this.set(field, value);
			this.rerender();
			this.autoAdvance();
		}

		setMeeting(kind) {
			const s = this.state;
			const tz = kind === 'in-person' ? this.cfg.inPerson.timeZone : safeTz(visitorTz());
			const changed = s.meeting !== kind || s.timeZone !== tz;
			this.set('meeting', kind);
			if (changed) {
				s.timeZone = tz;
				s.date = nextWeekday(todayKey(tz));
				s.month = s.date.slice(0, 7);
				s.slot = null;
				s.slots = {};
				s.slotsStatus = 'idle';
				s.submitError = '';
			}
		}

		chipGroup(labelKey, items, field, multi) {
			const s = this.state;
			return el(
				'fieldset',
				{ class: 'bj-booking__group' },
				el('legend', { class: 'bj-booking__eyebrow', text: this.str(labelKey) }),
				el(
					'div',
					{ class: 'bj-booking__chips' },
					items.map((value) => {
						const pressed = multi ? s[field].includes(value) : s[field] === value;
						return el('button', {
							type: 'button',
							class: 'bj-booking__chip',
							'aria-pressed': pressed ? 'true' : 'false',
							text: value,
							dataset: { key: `chip:${field}:${value}` },
							onclick: () => {
								if (multi) {
									const list = s[field].includes(value)
										? s[field].filter((v) => v !== value)
										: s[field].concat(value);
									this.set(field, list);
								} else {
									this.set(field, value);
								}
								this.rerender();
								if (!multi && s.budget && s.timeline && this.currentStep() === 'budget') this.autoAdvance();
							},
						});
					})
				)
			);
		}

		renderBudget() {
			return el(
				'div',
				{ class: 'bj-booking__stack' },
				this.chipGroup('budget', budgetList(this.state, this.cfg.options), 'budget', false),
				this.chipGroup('timeline', this.cfg.options.timeline, 'timeline', false)
			);
		}

		renderDropzone(labelKey) {
			if (!this.cfg.uploads) return null;
			// v1: visual only — no storage provider is wired yet.
			return el(
				'div',
				{ class: 'bj-booking__dropzone', 'aria-hidden': 'true' },
				el('span', { class: 'bj-booking__dropzone-icon' }, this.icon('document')),
				el('span', { class: 'bj-booking__dropzone-label', text: this.str(labelKey) }),
				el('span', { class: 'bj-booking__dropzone-helper', text: this.str('upload_types') })
			);
		}

		renderNotesStep(step) {
			const s = this.state;
			const field = step === 'ring-upload' ? 'existingRingDetails' : 'description';
			const textarea = el('textarea', {
				class: 'bj-booking__textarea',
				rows: '4',
				placeholder: this.str('textarea_placeholder'),
				'aria-label': this.str('textarea_placeholder'),
				dataset: { key: `notes:${field}` },
				oninput: (e) => {
					s[field] = e.target.value;
				},
			});
			textarea.value = s[field];

			return el(
				'div',
				{ class: 'bj-booking__stack' },
				step === 'details' ? this.chipGroup('collection', this.cfg.options.collection, 'collection', true) : null,
				step === 'details' ? this.chipGroup('stone_shape', this.cfg.options.stone, 'stone', true) : null,
				this.renderDropzone(step === 'ring-upload' ? 'upload_file' : 'upload_inspiration'),
				textarea,
				/* Every answer on these steps is optional, so CONTINUE alone is
				   enough — the Figma's extra "Skip" link read as a second, weaker
				   CTA (Bernard, 2026-10-16) and was dropped. */
				el('button', {
					type: 'button',
					class: 'button button--primary bj-booking__cta',
					text: this.str('continue'),
					dataset: { key: 'continue' },
					onclick: () => this.advance(),
				})
			);
		}

		/* ---- Contact -------------------------------------------------- */

		validateField(name) {
			const s = this.state;
			const value = String(s[name] || '').trim();
			let message = '';
			if (name === 'firstName' && !value) message = this.str('err_first');
			else if (name === 'lastName' && !value) message = this.str('err_last');
			else if (name === 'email') {
				if (!value) message = this.str('err_email_required');
				else if (!EMAIL_RE.test(value)) message = this.str('err_email');
			} else if (name === 'phone') {
				if (!value) message = this.str('err_phone_required');
				else if (!PHONE_RE.test(value)) message = this.str('err_phone');
			}
			if (message) s.errors[name] = message;
			else delete s.errors[name];
			return message;
		}

		isContactValid() {
			const s = this.state;
			return !!(
				s.firstName.trim() &&
				s.lastName.trim() &&
				s.phone.trim() &&
				PHONE_RE.test(s.phone.trim()) &&
				EMAIL_RE.test(s.email.trim())
			);
		}

		renderContact() {
			const s = this.state;
			const tpl = this.templates.contact;
			if (!tpl) return el('div');
			const frag = tpl.content.cloneNode(true);
			const form = frag.querySelector('[data-bj-booking-form]');
			const continueBtn = frag.querySelector('[data-bj-booking-continue]');
			if (continueBtn) continueBtn.setAttribute('data-key', 'continue');

			const country = frag.querySelector('[data-bj-field="country"]');
			const stateSel = frag.querySelector('[data-bj-field="state"]');
			if (country) this.arrangeCountries(country);

			/* Dial-code picker: options are "<name> (<dial>)", value "<dial>|<iso>"
			   so two countries sharing +1 stay distinct; the visible span shows
			   only the code. */
			const codeSel = frag.querySelector('[data-bj-field="countryCode"]');
			const codeVal = frag.querySelector('[data-bj-phone-code-value]');
			const codes = this.cfg.options.countryCodes || [];
			const setCode = (dial, iso) => {
				s.countryCode = dial;
				s.countryIso = iso;
				if (codeSel) codeSel.value = `${dial}|${iso}`;
				if (codeVal) codeVal.textContent = dial;
			};
			if (codeSel) {
				codes.forEach((c) => {
					codeSel.append(el('option', { value: `${c.dial}|${c.iso}`, text: `${c.name} (${c.dial})` }));
				});
				setCode(s.countryCode, s.countryIso);
				if (!codeSel.value && codes.length) setCode(codes[0].dial, codes[0].iso);
				codeSel.addEventListener('change', () => {
					const [dial, iso] = codeSel.value.split('|');
					s.codeTouched = true;
					setCode(dial, iso);
				});
			}

			const fillStates = () => {
				if (!stateSel) return;
				const placeholder = stateSel.querySelector('option[value=""]');
				stateSel.replaceChildren();
				if (placeholder) stateSel.append(placeholder);
				let provinces = [];
				const source =
					(country && s.country && country.querySelector(`option[value="${CSS.escape(s.country)}"]`)) ||
					(country && !s.country && country.querySelector('option[value="United States"]'));
				try {
					provinces = JSON.parse((source && source.dataset.provinces) || '[]');
				} catch (e) {
					provinces = [];
				}
				provinces.forEach((p) => {
					const name = Array.isArray(p) ? p[0] : p;
					stateSel.append(el('option', { value: name, text: name }));
				});
				stateSel.append(el('option', { value: this.str('state_other'), text: this.str('state_other') }));
				if (s.state && !stateSel.querySelector(`option[value="${CSS.escape(s.state)}"]`)) s.state = '';
				stateSel.value = s.state;
			};

			const showError = (name) => {
				const input = frag.querySelector(`[data-bj-field="${name}"]`) || form.querySelector(`[data-bj-field="${name}"]`);
				const err = form.querySelector(`[data-bj-error-for="${name}"]`);
				const message = s.errors[name] || '';
				if (err) {
					err.textContent = message;
					err.hidden = !message;
				}
				if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
			};

			const syncContinue = () => {
				if (continueBtn) continueBtn.hidden = !this.isContactValid();
			};

			form.querySelectorAll('[data-bj-field]').forEach((input) => {
				const name = input.getAttribute('data-bj-field');
				input.setAttribute('data-key', `field:${name}`);
				if (name === 'countryCode') return; // wired above
				input.value = s[name] || '';
				input.addEventListener('input', () => {
					s[name] = input.value;
					if (s.errors[name]) {
						delete s.errors[name];
						showError(name);
					}
					syncContinue();
				});
				input.addEventListener('change', () => {
					s[name] = input.value;
					if (name === 'country') {
						fillStates();
						// Follow the chosen country's dial code until the visitor
						// overrides it by hand.
						const match = !s.codeTouched && codes.find((c) => c.name === s.country);
						if (match) setCode(match.dial, match.iso);
					}
					syncContinue();
				});
				input.addEventListener('blur', () => {
					if (['firstName', 'lastName', 'email', 'phone'].includes(name)) {
						this.validateField(name);
						showError(name);
					}
				});
			});

			if (country) country.value = s.country;
			fillStates();
			['firstName', 'lastName', 'email', 'phone'].forEach((name) => {
				if (s.errors[name]) showError(name);
			});
			syncContinue();

			form.addEventListener('submit', (e) => {
				e.preventDefault();
				['firstName', 'lastName', 'email', 'phone'].forEach((name) => {
					this.validateField(name);
					showError(name);
				});
				if (this.isContactValid()) {
					this.advance();
				} else {
					const firstBad = form.querySelector('[aria-invalid="true"]');
					if (firstBad) firstBad.focus();
				}
			});

			return frag;
		}

		/* all_country_option_tags lists every country alphabetically; the
		   design leads with United States and Canada, then a divider. */
		arrangeCountries(select) {
			const placeholder = select.querySelector('option[value=""]');
			const leaders = ['United States', 'Canada']
				.map((name) => select.querySelector(`option[value="${name}"]`))
				.filter(Boolean);
			if (!leaders.length) return;
			const divider = el('option', { disabled: true, text: '──────────' });
			const anchor = placeholder ? placeholder.nextSibling : select.firstChild;
			leaders.forEach((opt) => select.insertBefore(opt, anchor));
			select.insertBefore(divider, anchor);
		}

		/* ---- Time ----------------------------------------------------- */

		eventRef() {
			return this.ids[this.state.meeting] || null;
		}

		async loadMonth({ force } = {}) {
			const s = this.state;
			const ref = this.eventRef();
			if (!ref || !s.month) return;
			const month = s.month;
			s.slotsStatus = 'loading';
			this.rerender();
			try {
				const all = await CAL.slots(ref, s.timeZone, month, { force });
				if (this.state !== s || s.month !== month || this.currentStep() !== 'time') return;
				// Belt and braces: never offer in-person days past the cutoff.
				const until = s.meeting === 'in-person' ? this.inPersonUntil() : '';
				const days = until ? Object.fromEntries(Object.entries(all).filter(([d]) => d <= until)) : all;
				s.slots = days;
				s.slotsStatus = 'ready';
				const today = todayKey(s.timeZone);
				const available = Object.keys(days).filter((d) => d >= today).sort();
				if (!days[s.date] || s.date < today) {
					s.date = available.length ? available[0] : '';
					if (!(s.slot && s.date && days[s.date] && days[s.date].includes(s.slot.start))) s.slot = null;
				}
			} catch (err) {
				if (err && err.name === 'AbortError') return;
				if (this.state !== s || s.month !== month) return;
				s.slots = {};
				s.slotsStatus = 'error';
			}
			if (this.currentStep() === 'time') this.rerender();
		}

		renderTime() {
			const s = this.state;
			const tz = s.timeZone;
			const today = todayKey(tz);
			const { lead, days } = monthGrid(s.month);
			const loading = s.slotsStatus === 'loading';
			const thisMonth = today.slice(0, 7);
			const until = s.meeting === 'in-person' ? this.inPersonUntil() : '';
			const lastMonth = until ? until.slice(0, 7) : '';
			// On or past the cutoff month: say in-person is ending instead of
			// letting empty months imply the location is open.
			const closing = !!until && s.month >= lastMonth;

			const navBtn = (dir, key, iconName, disabled) =>
				el(
					'button',
					{
						type: 'button',
						class: 'bj-booking__cal-nav',
						'aria-label': this.str(key),
						disabled: disabled ? true : null,
						dataset: { key: `nav:${dir}` },
						onclick: () => {
							s.month = shiftMonth(s.month, dir);
							s.slotsStatus = 'idle';
							s.slots = {};
							this.loadMonth();
						},
					},
					this.icon(iconName)
				);

			const grid = el(
				'div',
				{ class: 'bj-booking__cal-grid', role: 'grid' },
				Array.from({ length: lead }, () => el('span', { class: 'bj-booking__cal-blank', 'aria-hidden': 'true' })),
				days.map((day) => {
					const enabled =
						!loading && s.slotsStatus === 'ready' && day >= today && (!until || day <= until) && !!s.slots[day];
					return el('button', {
						type: 'button',
						class: 'bj-booking__day',
						text: String(Number(day.slice(-2))),
						'aria-label': dayAria(day),
						'aria-pressed': s.date === day ? 'true' : 'false',
						disabled: enabled ? null : true,
						dataset: { date: day, key: `day:${day}` },
						onclick: () => {
							s.date = day;
							s.slot = null;
							s.submitError = '';
							this.rerender();
						},
					});
				})
			);

			let stateLine = null;
			if (s.slotsStatus === 'error') {
				stateLine = el(
					'p',
					{ class: 'bj-booking__cal-state bj-booking__cal-state--error', role: 'alert' },
					`${this.str('load_error')} `,
					el('button', {
						type: 'button',
						class: 'bj-booking__retry',
						text: this.str('retry'),
						dataset: { key: 'retry' },
						onclick: () => this.loadMonth({ force: true }),
					})
				);
			} else if (s.slotsStatus === 'ready' && !Object.keys(s.slots).some((d) => d >= today)) {
				stateLine = el('p', {
					class: 'bj-booking__cal-state bj-booking__cal-state--empty',
					role: 'status',
					text: closing ? this.inPersonClosedNote() : this.str('no_availability'),
				});
			} else if (closing && s.slotsStatus === 'ready') {
				stateLine = el('p', { class: 'bj-booking__cal-state bj-booking__cal-state--note', role: 'status', text: this.inPersonClosedNote() });
			}

			const calendar = el(
				'div',
				{ class: `bj-booking__cal${loading ? ' is-loading' : ''}`, 'aria-busy': loading ? 'true' : 'false' },
				el(
					'div',
					{ class: 'bj-booking__cal-header' },
					navBtn(-1, 'prev_month', 'chevron-left', s.month <= thisMonth),
					el('p', { class: 'bj-booking__cal-month', text: monthLabel(s.month), 'aria-live': 'polite' }),
					navBtn(1, 'next_month', 'chevron-right', !!lastMonth && s.month >= lastMonth)
				),
				el(
					'div',
					{ class: 'bj-booking__cal-weekdays', 'aria-hidden': 'true' },
					['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => el('span', { text: d }))
				),
				grid,
				stateLine
			);

			const tzText =
				s.meeting === 'in-person' ? this.str('tz_eastern') : tzLabel(tz, this.str('tz_suffix'));
			const info = el(
				'div',
				{ class: 'bj-booking__time-info' },
				el('p', { class: 'bj-booking__meta', text: `${this.cfg.durationLabel} · ${tzText}` }),
				s.meeting === 'in-person' && this.cfg.inPerson.address
					? el('p', { class: 'bj-booking__address', text: this.cfg.inPerson.address })
					: null
			);

			const starts = (s.date && s.slots[s.date]) || [];
			const slots = starts.length
				? el(
						'div',
						{ class: 'bj-booking__slots', role: 'group', 'aria-label': this.str('time_title') },
						starts.map((start) =>
							el('button', {
								type: 'button',
								class: 'bj-booking__slot',
								text: fmtTime(start, tz),
								'aria-pressed': s.slot && s.slot.start === start ? 'true' : 'false',
								dataset: { start, key: `slot:${start}` },
								onclick: () => {
									s.slot = { start };
									s.submitError = '';
									s.booked = null;
									this.rerender();
								},
							})
						)
				  )
				: null;

			let submit = null;
			if (s.slot) {
				const label = s.submitting ? this.str('sending') : this.str('submit');
				submit = el(
					'div',
					{ class: 'bj-booking__submit' },
					this.renderConsent(s),
					el(
						'button',
						{
							type: 'button',
							class: `button button--primary bj-booking__cta${s.submitting ? ' is-loading' : ''}`,
							disabled: s.submitting ? true : null,
							'aria-busy': s.submitting ? 'true' : 'false',
							dataset: { key: 'submit' },
							onclick: () => this.submit(),
						},
						s.submitting ? this.spinner() : null,
						el('span', { text: label })
					),
					s.submitError ? el('p', { class: 'bj-booking__submit-error', role: 'alert', text: s.submitError }) : null
				);
			}

			return el('div', { class: 'bj-booking__time' }, calendar, info, slots, submit);
		}

		/* Marketing consent — an unticked checkbox above the submit button,
		   worded per path (bridal vs everything else). Shown only when the
		   Klaviyo list for this path is configured and the label has text;
		   the subscribe call fires only when it is ticked. */
		consentLabelFor(s) {
			const k = this.cfg.klaviyo;
			const bridal = s.path === 'Engagement rings' || s.path === 'Wedding band';
			return ((bridal ? k.consentBridal : k.consentOther) || '').trim();
		}

		renderConsent(s) {
			const k = this.cfg.klaviyo;
			const label = this.consentLabelFor(s);
			if (!k.companyId || !this.klaviyoListFor(s) || !label) return null;
			const id = 'bj-booking-consent';
			return el(
				'label',
				{ class: 'bj-booking__consent', for: id },
				el('input', {
					type: 'checkbox',
					id,
					class: 'bj-booking__consent-box',
					checked: s.marketingOptIn ? true : null,
					disabled: s.submitting ? true : null,
					dataset: { key: 'consent' },
					onchange: (e) => {
						s.marketingOptIn = !!e.target.checked;
					},
				}),
				el('span', { class: 'bj-booking__consent-text', text: label })
			);
		}

		/* ---- Submit --------------------------------------------------- */

		phoneE164() {
			const s = this.state;
			const digits = s.phone.replace(/\D/g, '');
			const code = (s.countryCode || '+1').replace(/[^\d+]/g, '');
			return `${code.startsWith('+') ? code : `+${code}`}${digits}`;
		}

		summaryRows() {
			const s = this.state;
			return {
				category: pathLabel(s.path),
				approach: s.approach,
				ring: s.existingRing,
				budget: s.budget,
				timeline: s.timeline,
				collection: s.collection.join(', '),
				stone: s.stone.join(', '),
				location: [s.city, s.state, s.country].map((v) => v.trim()).filter(Boolean).join(', '),
			};
		}

		formatNotes() {
			const s = this.state;
			const rows = this.summaryRows();
			const lines = [];
			const push = (label, value) => {
				if (value) lines.push(`${label}: ${value}`);
			};
			push(this.str('category'), rows.category);
			push(this.str('approach'), rows.approach);
			push(this.str('ring'), rows.ring);
			push(this.str('budget'), rows.budget);
			push(this.str('timeline'), rows.timeline);
			push(this.str('collection'), rows.collection);
			push(this.str('stone_shape'), rows.stone);
			push(this.str('meeting_type'), s.meeting === 'in-person' ? this.str('meeting_in_person') : this.str('meeting_virtual'));
			push(this.str('phone'), this.phoneE164());
			push(this.str('location'), rows.location);
			const notes = [s.existingRingDetails, s.description].map((v) => v.trim()).filter(Boolean).join('\n');
			if (notes) lines.push('', `${this.str('project_notes')}:`, notes);
			return lines.join('\n');
		}

		buildPayload() {
			const s = this.state;
			const rows = this.summaryRows();
			const metadata = {};
			Object.keys(rows).forEach((k) => {
				if (rows[k]) metadata[k] = String(rows[k]).slice(0, 500);
			});
			metadata.meeting = s.meeting;
			metadata.source = 'storefront-drawer';
			return Object.assign({ start: new Date(s.slot.start).toISOString() }, this.eventRef().params, {
				attendee: {
					name: `${s.firstName.trim()} ${s.lastName.trim()}`.trim(),
					email: s.email.trim(),
					timeZone: s.timeZone,
					phoneNumber: this.phoneE164(),
					language: 'en',
				},
				bookingFieldsResponses: { notes: this.formatNotes() },
				metadata,
			});
		}

		async submit() {
			const s = this.state;
			if (s.submitting || !s.slot) return;

			if (!E164_RE.test(this.phoneE164())) {
				s.errors.phone = this.str('err_phone');
				this.go(computeSteps(s).indexOf('contact'), -1);
				return;
			}

			s.submitting = true;
			s.submitError = '';
			this.rerender();
			this.emit('submit');

			const month = s.month;
			try {
				const data = await CAL.book(this.buildPayload());
				if (this.state !== s) return;
				s.submitting = false;
				s.booked = data || {};
				this.go(computeSteps(s).indexOf('confirmation'), 1);
				this.emit('success', { uid: data && data.uid });
				this.subscribeToKlaviyo(s);
			} catch (err) {
				if (this.state !== s) return;
				s.submitting = false;
				s.submitError = this.str('submit_error');
				const message = String((err && err.message) || '');
				const conflict =
					(err && err.status === 409) ||
					/not available|already|slot|conflict|out of bounds|no longer/i.test(message);
				this.emit('error', { status: err && err.status, message });
				if (conflict) {
					s.slot = null;
					CAL.invalidate(this.eventRef(), s.timeZone, month);
					this.loadMonth({ force: true });
				} else {
					this.rerender();
				}
				const alert = this.stepEl.querySelector('.bj-booking__submit-error');
				if (alert) {
					alert.setAttribute('tabindex', '-1');
					alert.focus({ preventScroll: true });
				}
			}
		}

		/* Engagement ring and wedding band answers are "bridal"; fine
		   jewelry and "Not sure" are not. Lists come from theme settings. */
		klaviyoListFor(s) {
			const k = this.cfg.klaviyo;
			const bridal = s.path === 'Engagement rings' || s.path === 'Wedding band';
			return (bridal ? k.listBridal : k.listOther) || '';
		}

		subscribeToKlaviyo(s) {
			const k = this.cfg.klaviyo;
			const listId = this.klaviyoListFor(s);
			const email = s.email.trim();
			if (!k.companyId || !listId || !email) return;
			// A visible consent checkbox left unticked means no subscription.
			if (this.consentLabelFor(s) && !s.marketingOptIn) return;
			const profile = {
				email,
				first_name: s.firstName.trim(),
				last_name: s.lastName.trim(),
				properties: {
					booking_project: s.path,
					booking_meeting: s.meeting,
					booking_source: 'storefront-drawer',
				},
			};
			const phone = this.phoneE164();
			if (E164_RE.test(phone)) profile.phone_number = phone;
			KLAVIYO.subscribe(k.companyId, listId, profile, 'Request appointment drawer').catch((err) => {
				if (window.console && console.warn) console.warn('[bj-booking] Klaviyo subscribe failed:', err);
			});
		}

		/* ---- Confirmation --------------------------------------------- */

		renderConfirmation() {
			const s = this.state;
			const rows = this.summaryRows();
			const tz = s.timeZone;
			const start = s.slot ? s.slot.start : '';
			const tzText = s.meeting === 'in-person' ? this.str('tz_eastern') : tzLabel(tz, this.str('tz_suffix'));

			const item = (labelKey, value, wide) =>
				value
					? el(
							'div',
							{ class: `bj-booking__summary-item${wide ? ' bj-booking__summary-item--wide' : ''}` },
							el('dt', { text: this.str(labelKey) }),
							el('dd', { text: value })
					  )
					: null;

			const section = (headingKey, items) =>
				el(
					'section',
					{ class: 'bj-booking__summary' },
					el('h3', { class: 'bj-booking__summary-heading', text: this.str(headingKey) }),
					el('dl', { class: 'bj-booking__summary-grid' }, items)
				);

			const notes = [s.existingRingDetails, s.description].map((v) => v.trim()).filter(Boolean).join(' · ');
			const logo = this.templates.logo ? this.templates.logo.content.cloneNode(true) : null;
			const first = s.firstName.trim();

			return el(
				'div',
				{ class: 'bj-booking__confirmation' },
				el(
					'div',
					{ class: 'bj-booking__done' },
					logo ? el('div', { class: 'bj-booking__logo', 'aria-hidden': 'true' }, logo) : null,
					el('h3', {
						class: 'bj-booking__done-title',
						id: 'BjBookingStepTitle',
						tabindex: '-1',
						text: first ? this.str('all_set', { name: first }) : this.str('all_set_anon'),
					}),
					this.cfg.confirmationNote ? el('p', { class: 'bj-booking__done-note', text: this.cfg.confirmationNote }) : null
				),
				section('project', [
					item('category', rows.category),
					item('approach', rows.approach),
					item('ring', rows.ring),
					item('budget', rows.budget),
					item('timeline', rows.timeline),
					item('collection', rows.collection),
					item('stone_shape', rows.stone),
					item('project_notes', notes, true),
				]),
				section('contact', [
					item('name', `${s.firstName.trim()} ${s.lastName.trim()}`.trim()),
					item('email', s.email.trim()),
					item('phone', this.phoneE164()),
					item('location', rows.location),
				]),
				section('pending_appointment', [
					item('meeting_type', s.meeting === 'in-person' ? this.str('meeting_in_person') : this.str('meeting_virtual')),
					item('date', start ? fmtLongDate(start, tz) : ''),
					item('time', start ? fmtTime(start, tz) : ''),
					item('timezone', tzText),
				]),
				el(
					'div',
					{ class: 'bj-booking__done-actions' },
					el('button', {
						type: 'button',
						class: 'button button--primary bj-booking__cta',
						text: this.str('done'),
						dataset: { key: 'done' },
						onclick: () => this.forceClose(),
					})
				)
			);
		}
	}

	customElements.define('bj-booking-drawer', BjBookingDrawer);

	/* ------------------------------------------------------------------ */
	/* Triggers                                                             */
	/* ------------------------------------------------------------------ */

	function drawer() {
		const node = document.getElementById('BjBookingDrawer');
		return node instanceof BjBookingDrawer && node.ready ? node : null;
	}

	document.addEventListener('click', (event) => {
		if (event.defaultPrevented || event.button > 0) return;
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

		const explicit = event.target.closest('[data-bj-booking-open]');
		const link = explicit || event.target.closest('a[href]');
		if (!link) return;

		const target = drawer();
		if (!target) return;

		if (!explicit) {
			let url;
			try {
				url = new URL(link.href, window.location.origin);
			} catch (e) {
				return;
			}
			if (url.origin !== window.location.origin) return;
			if (!url.pathname.replace(/\/$/, '').endsWith(target.triggerPath)) return;
		}

		event.preventDefault();
		target.openFrom(link);
	});

	/* window.bjOpenBooking(opts) -> boolean: true when the drawer exists, is
	   configured and opened; false so callers can follow their link. */
	window.bjOpenBooking = function (opts) {
		const target = drawer();
		if (!target) return false;
		return target.openFrom((opts && opts.trigger) || document.activeElement);
	};
})();
